<template>
    <div>
        <b-form-group
            label="Question"
            label-for="question"
        >
            <TipTap
                v-model="editable_content.question"
                :class="{'is-invalid': validating && !editable_content.question}"
                :menu_items="['bold', 'italic', 'underline', 'bullet_list', 'ordered_list', 'image', 'edit_html']"
                @input="setContent('question', $event)"
            />

            <b-form-invalid-feedback>
                Question is required
            </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
            label="Sortable Items"
            label-for="sortable_items"
        >
            <draggable v-model="editable_content.options" handle=".drag-handle">
                <div
                    v-for="(item, index) in editable_content.options"
                    :key="index"
                    class="mb-2"
                >
                    <div v-if="item.type =='text'">
                        <b-input-group
                            :state="validating && (!editable_content.options || editable_content.options && editable_content.options.length < 2) ? false : null"
                        >
                            <b-input-group-prepend v-if="editable_content.options && editable_content.options.length > 1" class="drag-handle" is-text>
                                <font-awesome-icon :icon="['far', 'arrows-v']" />
                            </b-input-group-prepend>

                            <b-form-input
                                type="text"
                                v-model="item.text"
                                :state="validating && !item.text ? false : null"
                                v-focus
                            />

                            <b-input-group-append>
                                <b-button variant="danger" @click="deleteItem(index)">
                                    <font-awesome-icon :icon="['far', 'minus']" />
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>

                        <b-form-invalid-feedback class="mb-2" :state="validating && !item.text ? false : null">
                            Item text is required
                        </b-form-invalid-feedback>
                    </div>
                    <div v-if="item.type == 'image'" class="d-flex align-items-center">
                        <div class="drag-handle mx-3">
                            <font-awesome-icon :icon="['far', 'arrows-v']" />
                        </div>
                        <div class="mr-3">
                            <SbpMedia
                                :media="item"
                                :type="'sorting'"
                                :key="item.image"
                                @update="update($event, item)"
                                @removeImage="deleteItem(index)"
                            />
                        </div>
                        <div class="ml-auto">
                            <b-button variant="danger" @click="deleteItem(index)">
                                <font-awesome-icon :icon="['far', 'minus']" />
                            </b-button>
                        </div>
                    </div>
                </div>
            </draggable>

            <b-form-invalid-feedback class="mb-2" :state="validating && (!editable_content.options || editable_content.options && editable_content.options.length < 2) ? false : null">
                At least 2 sortable items are required
            </b-form-invalid-feedback>

            <div class="d-flex">
                <div class="mr-1">
                    <b-btn size="sm" variant="outline-primary" @click="addItem('text')">
                        <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Text Item
                    </b-btn>
                </div>
                    <div>
                        <b-dropdown
                            size="sm"
                            variant="outline-primary"
                            dropup
                            right
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Image Item
                            </template>
                            <b-dropdown-item @click.prevent="addItem('image', 'sbp')">
                                <font-awesome-icon :icon="['fad', 'plus-square']" class="text-primary" fixed-width /> Add SBP Media Image
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
            </div>
        </b-form-group>

        <b-form-group
            label="Direction"
            label-for="direction"
        >
            <b-form-radio-group
                v-model="editable_content.direction"
                :options="direction_options"
                :state="validating && !editable_content.direction ? false : null"
            >
                <b-form-invalid-feedback
                    :state="validating && !editable_content.direction ? false : null"
                >
                    Direction is required
                </b-form-invalid-feedback>
            </b-form-radio-group>
        </b-form-group>

        <add-related-media-modal
            v-model="show_add_related_media_modal"
            :type="'image'"
            @link="linkAsset"
            @hide="show_add_related_media_modal = false"
        />
    </div>
</template>

<style lang="scss" scoped>
@import '@/assets/css/_colors';

.sortable-chosen {
    .input-group-text {
        border-color: $warning;
    }
    .form-control {
        // border-left-color: $warning;
        border-top-color: $warning;
        border-bottom-color: $warning;
    }
}
</style>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import AddRelatedMediaModal from '@/components/modals/article/related_media/AddRelatedMediaModal'
import SbpMedia from '@/components/media/SbpMedia'

export default {
    data: () => ({
        direction_options: [
            { text: 'Vertical', value: 'vertical' },
            { text: 'Horizontal', value: 'horizontal' }
        ],
        editable_content: {
            options: []
        },
        show_add_related_media_modal: false
    }),
    props: [
        'question'
    ],
    components: {
        draggable,
        TipTap: () => import('@/components/tiptap/TipTap'),
        AddRelatedMediaModal,
        SbpMedia
    },
    computed: {
        ...mapState({
            validating: state => state.utilities.validating
        })
    },
    watch: {
        question: {
            handler (question) {
                if (
                    question &&
                    question.content
                ) {
                    this.editable_content = question.content
                }
            },
            deep: true,
            immediate: true
        },
        editable_content: {
            handler (editable_content) {
                if (editable_content) {
                    this.$set(this.question, 'content', editable_content)
                }
            },
            deep: true
        }
    },
    methods: {
        addItem (type, imageSource = '') {
            if (type == 'text') {
                if (!this.editable_content.options) {
                    this.editable_content.options = []
                }

                this.editable_content.options.push({
                    type: type
                })
            } else if (type == 'image') {
                if (imageSource == 'sbp') {
                    this.show_add_related_media_modal = true
                }
            }
        },
        deleteItem (index) {
            this.$swal({
                html: 'Are you sure you delete this sorting item?',
                showCancelButton: true,
                confirmButtonText: 'Remove Item',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    if (this.editable_content.options) {
                        this.editable_content.options.splice(index, 1)
                    }
                }
            })
        },
        update (media, item) {
            if (media.merlin_id) {
                let merlinId = parseInt(media.merlin_id)
                item.merlin_id = !isNaN(merlinId) ? merlinId : null
                item.media_id = !isNaN(merlinId) ? merlinId : null
            } else if (media.related_media_id) {
                let mediaId = parseInt(media.related_media_id)
                item.related_media_id = !isNaN(mediaId) ? mediaId : null
                item.media_id = !isNaN(mediaId) ? mediaId : null
            }
            item.type = media.type
            item.image = media.src ? media.src : media.image
            item.alt = media.alt
            item.credit = media.credit
            item.description = media.description
            item.caption = media.caption
            item.name = media.name ? media.name : (media.title ? media.title : '')
        },
        linkAsset (asset) {
            if (!this.editable_content.options) {
                this.editable_content.options = []
            }

            let merlinId = parseInt(asset.merlinid)
            let alt = asset.alt || asset.headline
            let image

            if (asset.url) {
                image = asset.url
            } else if (asset.thumbpermlink) {
                image = asset.thumbpermlink
            } else {
                image = asset.previewpermlink
            }

            const option = {
                type: 'image',
                image,
                alt
            };

            if (!isNaN(merlinId)) {
                option.merlin_id = merlinId
                option.title = asset.title
            } else {
                option.related_media_id = asset.id
                option.title = asset.title
                option.description = asset.description
                option.caption = asset.caption
                option.credit = asset.credit
            }

            this.editable_content.options.push(option);
            this.show_add_related_media_modal = false
        },
        setContent (field, value) {
            this.$set(this.editable_content, field, value)
        }
    }
}
</script>
